<template>
  <b-modal ref="edit-discount-modal"
           hide-footer
           scrollable
           hide-header-close
           no-close-on-backdrop
           size="lg"
           title="Discount Edit"
           @hidden="showDiscountTypeSelector = false">

    <template #modal-header>
      <h5>{{ $t("#TR-discount-edit") }}</h5>
      <b-button v-if="showDiscountTypeSelector"
                size="sm"
                variant="outline-danger"
                type="button"
                @click="showDiscountTypeSelector = false">
        <span class="mdi mdi-close"></span> {{ $t("#TR-cancel") }}
      </b-button>
    </template>

    <div v-if="saveLoader" class="small-loader">
      <i class="mdi mdi-rotate-right mdi-spin"></i>
    </div>

    <div v-if="discount">
      <div class="row mb-2">

        <div class="col-12 col-sm-6">
          <label> {{ $t("#TR-name") }}:</label>
          <b-form-input v-model="discount['name']"></b-form-input>
        </div>

        <div class="col-12 col-sm-6">
          <label> {{ $t("#TR-discount-type") }}:</label>
          <br>
          <b-button block variant="success"
                    @click="showDiscountTypeSelector = !showDiscountTypeSelector">
            {{ discountType.name }}
          </b-button>
        </div>

      </div>

      <div class="row mb-2">
        <div class="col-12 col-sm-6">
          <label>{{ $t("#TR-count-type") }}</label>
          <b-dropdown
            block
            split-variant="outline-success"
            variant="success"
            :text="discount.countType"
            menu-class="w-100"
            class="w-100">
            <b-dropdown-item href="#"
                             @click="discount.countType = 'Amount'">
              {{ $t("#TR-amount") }}
            </b-dropdown-item>
            <b-dropdown-item href="#"
                             @click="discount.countType = 'Percent'">
              {{ $t("#TR-percent") }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div class="col-12 col-sm-6">
          <label> {{ $t("#TR-value") }} <i v-if="discount.countType === 'Percent'">(%)</i>:</label>
          <b-form-input
            v-model="displayValue"
            type="text"
            @blur="isInputActive = false"
            @focus="isInputActive = true">
          </b-form-input>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12 col-sm-6">
          <label>{{ $t("#TR-starting-sale") }}:</label>
          <datetime v-model="discount.startSales.value"
                    input-id="'start'"
                    format="yyyy-MM-dd HH:mm:ss"
                    :disabled="!discount.startSales.enabled"
                    type="datetime"
                    input-class="form-control"
                    class="theme-green">
          </datetime>
        </div>

        <div class="col-12 col-sm-6">
          <label>{{ $t("#TR-ending-sale") }}:</label>
          <datetime v-model="discount.stopSales.value"
                    input-id="'stop'"
                    format="yyyy-MM-dd HH:mm:ss"
                    :disabled="!discount.stopSales.enabled"
                    type="datetime"
                    input-class="form-control"
                    class="theme-green">
          </datetime>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12 col-sm-6">
          <b-form-checkbox v-model="discount.startSales.enabled"
                           switch
                           size="lg">
            <span class="small">{{ $t("#TR-enabled") }}</span>
          </b-form-checkbox>
        </div>
        <div class="col-12 col-sm-6">
          <b-form-checkbox v-model="discount.stopSales.enabled"
                           switch
                           size="lg">
            <span class="small">{{ $t("#TR-enabled") }}</span>
          </b-form-checkbox>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12 col-sm-6">
          <label>{{ $t("#TR-classification") }}:</label>
          <b-dropdown
            block
            split-variant="outline-success"
            variant="success"
            :text="discount.classification ? classificationsList.bySifr[discount['classification']].name : 'Empty'"
            menu-class="w-100"
            class="w-100">
            <b-dropdown-item href="#"
                             @click="discount.classification = null">
              {{ $t("#TR-empty") }}
            </b-dropdown-item>
            <b-dropdown-item v-for="(classification, idx) in classificationsList.byIndex"
                             :key="idx"
                             href="#"
                             @click="discount.classification = classification['sifr']">
              {{ classification.name }}
            </b-dropdown-item>

          </b-dropdown>
        </div>
        <div class="col-12 col-sm-6">
          <b-form-checkbox v-model="discount.needManager"
                           switch
                           size="lg">
            <span class="small">{{ $t("#TR-need-manager") }}</span>
          </b-form-checkbox>
          <b-form-checkbox v-model="discount.prohibitHand"
                           switch
                           size="lg">
            <span class="small">{{ $t("#TR-restrict-manual") }}</span>
          </b-form-checkbox>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-12 text-center">
          <b-button
            variant="danger"
            type="button"
            class="mx-1"
            @click="cancel">
            <span class="mdi mdi-close mr-1"></span> {{ $t("#TR-cancel") }}
          </b-button>
          <b-button
            variant="success"
            type="button"
            class="mx-1"
            @click="onSave()">
            <span class="mdi mdi-content-save"></span> {{ $t("#TR-save") }}
          </b-button>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-12 text-center">
          <b-alert :show="formError.length > 0"
                   variant="danger"
                   class="mb-0">
            <ul class="small mb-0">
              <li v-for="(err, idx ) in formError" :key="idx">{{ err }}</li>
            </ul>
          </b-alert>
        </div>
      </div>
    </div>

    <div v-if="showDiscountTypeSelector"
         class="discount-type-selector">

      <div class="discount-type text-success font-weight-bold mb-2">{{ $t("#TR-select-discount-types") }}</div>

      <discount-type-list-tree
        :discount-type-list="discountTypes"
        @onSelectDiscountTypes="onSelectDiscountTypes">
      </discount-type-list-tree>
    </div>

  </b-modal>
</template>

<script>
  import { ApiEditor } from '@/services/api';
  import MSG from '@/services/notify';
  import DiscountTypeListTree from '@/views/WebEditor/Discounts/DiscountTypeListTree';

  export default {
    name: 'DiscountEdit',
    components: { DiscountTypeListTree },

    props: {
      editableDiscount: {
        default: () => {
          return null;
        },
        type: Object
      },
      selectedDiscountType: {
        default: () => {
          return null;
        },
        type: Object
      },
      classificationsList: {
        default: () => {
          return null;
        },
        type: Object
      },
      discountTypeList: {
        default: () => {
          return null;
        },
        type: Array
      }
    },
    data() {
      return {
        discount: null,
        formError: [],
        showDiscountTypeSelector: false,
        discountTypes: this.discountTypeList,
        discountType: this.selectedDiscountType ? this.selectedDiscountType : { name: 'Select discount type', sifr: null },
        saveLoader: false,
        isInputActive: false
      };
    },

    computed: {
      displayValue: {
        get: function () {
          if (this.isInputActive) {
            return this.discount.value.toString();
          } else {
            return '' + this.discount.value.toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,');
          }
        },
        set: function (modifiedValue) {
          let newValue = parseFloat(modifiedValue.replace(/[^\d.]/g, ''));
          if (isNaN(newValue)) {
            newValue = 0;
          }
          this.$emit('input', newValue);
          this.discount.value = newValue;
        }
      }
    },

    async created() {
      await this.getDiscount();
    },

    methods: {

      async getDiscount() {

        if (!this.editableDiscount.guid) {
          this.discount = this.newDiscount();
        } else {
          const result = await ApiEditor.getDiscount(this.editableDiscount.guid);

          if (result.error) {
            MSG.error(result.error, this);
            this.cancel();
          } else {
            this.discount = result.data;
          }
        }
      },

      newDiscount() {

        return {
          parent: this.selectedDiscountType ? this.selectedDiscountType.sifr : 0,
          needManager: false,
          prohibitHand: false,
          value: 0,
          startSales: {
            value: this.$moment().subtract(1, 'days').format(),
            enabled: false
          },
          stopSales: {
            value: this.$moment().format(),
            enabled: false
          },
          classification: null,
          countType: 'Amount',
          name: ''
        };
      },

      formatPrice(val) {

        console.log(val);

        val = (val / 1).toFixed(2);

        // val = val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        return val;
      },

      async onSave() {
        this.saveLoader = true;
        this.formError = this.validateDiscount();

        if (this.formError.length > 0) {
          return;
        }

        let result = {};
        if (this.discount.guid) {
          result = await ApiEditor.updateDiscount(this.discount);
        } else {
          result = await ApiEditor.addDiscount(this.discount);
        }

        if (!result.error) {
          MSG.success(this.$t('#TR-discount-saved'), this);
          this.$emit('discountSaved');
        } else {
          MSG.error(result.error, this);
        }

        this.saveLoader = false;
        this.cancel();
      },

      onSelectDiscountTypes(discountType) {
        this.showDiscountTypeSelector = false;
        this.discount.parent = discountType.sifr;
        this.discountType = discountType;
      },

      validateDiscount() {
        const error = [];

        if (!this.discount.name) {
          error.push(this.$t('#TR-enter-name'));
        }

        return error;
      },

      cancel() {
        this.showDiscountTypeSelector = false;
        this.$refs['edit-discount-modal'].hide();
        this.$emit('cancelViewEdit');
      },

      openDiscountEdit() {
        this.$refs['edit-discount-modal'].show();
      }
    }
  };
</script>

<style lang="stylus">

.discount-type-selector {
    width: 100%
    height: 100%
    background: #fff
    position: absolute
    top: 0
    left: 0
    z-index: 100
    padding: 10px 30px
}

.vdatetime-input {

}

.theme-green .vdatetime-popup__header,
.theme-green .vdatetime-calendar__month__day--selected > span > span,
.theme-green .vdatetime-calendar__month__day--selected:hover > span > span {
    background: c-accent;
}

.theme-green .vdatetime-year-picker__item--selected,
.theme-green .vdatetime-time-picker__item--selected,
.theme-green .vdatetime-popup__actions__button {
    color: c-accent;
}

  .darkmode {
    .discount-type-selector {
      background: c-dark-bg
    }
  }


</style>
